import React, { useState } from 'react';
import { X } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'for_review', label: 'For Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'failed', label: 'Failed' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'suggest_approve', label: 'Suggest Approve' },
  { value: 'suggest_exclude', label: 'Suggest Exclude' },
  { value: 'suggest_fail', label: 'Suggest Fail' },
];

const REASON_OPTIONS = [
  'Management approval',
  'AI false response',
  'No longer in business',
];

const DECISION_LABELS = {
  for_review: 'For Review',
  approved: 'Approved',
  failed: 'Failed',
  excluded: 'Excluded',
  suggest_approve: 'Suggest Approve',
  suggest_exclude: 'Suggest Exclude',
  suggest_fail: 'Suggest Fail',
};

const DECISION_COLORS = {
  for_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  excluded: 'bg-gray-100 text-gray-600',
  suggest_approve: 'bg-emerald-100 text-emerald-700',
  suggest_exclude: 'bg-slate-100 text-slate-600',
  suggest_fail: 'bg-rose-100 text-rose-700',
};

export const PolicyStatusModal = ({ policy, onClose, onUpdate }) => {
  const [selectedStatus, setSelectedStatus] = useState(policy.decision ?? '');
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const canSubmit = selectedStatus && selectedReasons.length > 0 && comment.trim();

  const toggleReason = (reason) => {
    setSelectedReasons(prev =>
      prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      await onUpdate(policy.id, { status: selectedStatus, reasons: selectedReasons, comment: comment.trim() });
      onClose();
    } catch {
      // error toast handled by the API layer
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Update Policy Status</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

          {/* Policy identifier + current decision */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Policy:{' '}
              <span className="font-medium text-gray-900 font-mono text-xs">
                {policy.policyId ?? policy.policy}
              </span>
            </p>
            {policy.decision && (
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${DECISION_COLORS[policy.decision] ?? 'bg-gray-100 text-gray-700'}`}>
                Current: {DECISION_LABELS[policy.decision] ?? policy.decision}
              </span>
            )}
          </div>

          <hr className="border-gray-200" />

          {/* Change Status */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Change Status to: <span className="text-red-500">*</span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedStatus(opt.value)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors text-left ${
                    selectedStatus === opt.value
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reasons */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Reason(s): <span className="text-red-500">*</span>
            </p>
            <div className="space-y-2">
              {REASON_OPTIONS.map(reason => (
                <label key={reason} className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedReasons.includes(reason)}
                    onChange={() => toggleReason(reason)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{reason}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Comment: <span className="text-red-500">*</span>
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Add a comment..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Update History */}
          {/* {policy.history?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Update History:</p>
              <div className="space-y-3">
                {policy.history.map((entry, i) => (
                  <div key={i} className="border-l-2 border-gray-200 pl-3 py-0.5">
                    <p className="text-xs text-gray-500 mb-0.5">
                      <span className="font-medium text-gray-700">{entry.user}</span>
                      {' — '}
                      {entry.date}
                    </p>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${DECISION_COLORS[entry.status] || 'bg-gray-100 text-gray-700'}`}>
                      {DECISION_LABELS[entry.status] ?? entry.status}
                    </span>
                    {entry.reasons?.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">{entry.reasons.join(', ')}</p>
                    )}
                    {entry.comment && (
                      <p className="text-xs text-gray-600 mt-0.5">{entry.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )} */}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className={`px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors ${
              canSubmit && !loading
                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                : 'bg-blue-300 cursor-not-allowed opacity-60'
            }`}
          >
            {loading ? 'Updating...' : 'Update Status'}
          </button>
        </div>
      </div>
    </div>
  );
};
